import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { engagementService } from '../services/api';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Setup date-fns localizer
const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export default function EngagementsCalendarPage() {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEngagements();
  }, []);

  const loadEngagements = async () => {
    try {
      const data = await engagementService.getAll();
      setEngagements(data);
    } catch (error) {
      console.error('Error loading engagements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Convert engagements to calendar events
  const events = useMemo(() => {
    return engagements
      .filter(eng => eng.startDate) // Only show engagements with a start date
      .map(eng => ({
        id: eng.id,
        title: eng.title,
        start: new Date(eng.startDate),
        end: eng.endDate ? new Date(eng.endDate) : new Date(eng.startDate),
        allDay: true,
        resource: eng,
      }));
  }, [engagements]);

  // Custom event styling based on status
  const eventStyleGetter = (event) => {
    const status = event.resource?.status;
    let backgroundColor = '#3b82f6'; // default blue
    
    switch (status) {
      case 'active':
        backgroundColor = '#22c55e'; // green
        break;
      case 'paused':
        backgroundColor = '#eab308'; // yellow
        break;
      case 'completed':
        backgroundColor = '#3b82f6'; // blue
        break;
      case 'cancelled':
        backgroundColor = '#ef4444'; // red
        break;
      default:
        backgroundColor = '#6b7280'; // gray
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: status === 'cancelled' ? 0.6 : 1,
        color: 'white',
        border: 'none',
        display: 'block',
        fontWeight: '500',
      },
    };
  };

  // Handle event click
  const handleSelectEvent = (event) => {
    navigate(`/engagements/${event.id}`);
  };

  // Handle slot selection (for creating new engagements)
  const handleSelectSlot = ({ start }) => {
    const dateStr = format(start, 'yyyy-MM-dd');
    navigate(`/engagements/new?startDate=${dateStr}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Engagements Calendar | CR Hood Admin</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Engagements Calendar</h1>
          <div className="flex gap-2">
            <Link to="/engagements" className="btn btn-secondary">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              List View
            </Link>
            <Link to="/engagements/new" className="btn btn-primary">
              New Engagement
            </Link>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-green-500"></span>
            <span>Active</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-yellow-500"></span>
            <span>Paused</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-blue-500"></span>
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-red-500 opacity-60"></span>
            <span>Cancelled</span>
          </div>
        </div>

        {/* Calendar */}
        <div className="card p-4">
          <style>{`
            .rbc-calendar {
              font-family: inherit;
            }
            .rbc-header {
              padding: 8px 4px;
              font-weight: 600;
              font-size: 0.875rem;
            }
            .rbc-month-view {
              border-radius: 8px;
              overflow: hidden;
            }
            .rbc-today {
              background-color: #eff6ff;
            }
            .rbc-event {
              padding: 2px 6px;
              font-size: 0.75rem;
            }
            .rbc-event:focus {
              outline: 2px solid #2563eb;
              outline-offset: 2px;
            }
            .rbc-toolbar button {
              padding: 6px 12px;
              border-radius: 6px;
              font-size: 0.875rem;
              font-weight: 500;
            }
            .rbc-toolbar button:hover {
              background-color: #f3f4f6;
            }
            .rbc-toolbar button.rbc-active {
              background-color: #2563eb;
              color: white;
            }
            .rbc-toolbar button.rbc-active:hover {
              background-color: #1d4ed8;
            }
            .rbc-off-range-bg {
              background-color: #f9fafb;
            }
            .rbc-date-cell {
              padding: 4px 8px;
              font-size: 0.875rem;
            }
            .rbc-show-more {
              color: #2563eb;
              font-size: 0.75rem;
              font-weight: 500;
            }
          `}</style>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 700 }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            popup
            views={['month', 'week', 'agenda']}
            defaultView="month"
            tooltipAccessor={(event) => `${event.title} (${event.resource?.status})`}
          />
        </div>

        {/* Tips */}
        <div className="text-sm text-gray-500">
          <p>💡 Click on an engagement to view details. Click on an empty date to create a new engagement.</p>
        </div>
      </div>
    </>
  );
}
